<template>
  <div
    class="chat-header d-flex align-center px-3"
    style="height: 64px; background: #fff; border-bottom: 1px solid #e0e0e0; flex-shrink: 0"
  >
    <!-- Back Button (mobile) -->
    <v-btn icon small class="d-md-none mr-1" @click="$emit('back')">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>

    <!-- Avatar (clickable for group info) -->
    <v-avatar
      size="42"
      class="mr-3"
      :style="chat.is_group ? 'cursor: pointer' : ''"
      @click="chat.is_group && $emit('toggleGroupInfo')"
    >
      <v-img v-if="chatAvatar" :src="chatAvatar" />
      <div
        v-else
        class="d-flex align-center justify-center white--text text-subtitle-1 font-weight-medium"
        :style="`background: ${avatarColor}; width: 100%; height: 100%; border-radius: 50%`"
      >
        {{ chatInitial }}
      </div>
    </v-avatar>

    <!-- Chat Name + Status (clickable for group info) -->
    <div
      class="flex-grow-1 overflow-hidden"
      :style="chat.is_group ? 'cursor: pointer' : ''"
      @click="chat.is_group && $emit('toggleGroupInfo')"
    >
      <div
        class="text-subtitle-1 font-weight-medium text-truncate"
        style="line-height: 1.4; color: #111"
      >
        {{ chatName }}
      </div>
      <div class="text-caption text-truncate" :class="onlineClass">
        {{ statusText }}
      </div>
    </div>

    <!-- Group info toggle button -->
    <v-btn
      v-if="chat.is_group"
      icon
      @click="$emit('toggleGroupInfo')"
    >
      <v-icon color="grey darken-1">mdi-information-outline</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

const AVATAR_COLORS = [
  '#e53935', '#d81b60', '#8e24aa', '#5e35b1',
  '#1e88e5', '#00897b', '#43a047', '#fb8c00',
  '#f4511e', '#6d4c41', '#546e7a', '#039be5',
];

export default {
  name: 'ChatHeader',

  props: {
    chat: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapGetters('chat', ['isUserOnline', 'getTypingUsers']),

    chatName() {
      if (this.chat.is_group) {
        return this.chat.name || 'Group Chat';
      }
      const other = this.chat.users?.find(u => u.id !== this.user.id);
      return other ? `${other.first_name} ${other.last_name}`.trim() : 'Chat';
    },

    chatAvatar() {
      if (this.chat.is_group) {
        return this.chat.image_path ? this.mediaUrl + this.chat.image_path : null;
      }
      const other = this.chat.users?.find(u => u.id !== this.user.id);
      return other?.image_path ? this.mediaUrl + other.image_path : null;
    },

    chatInitial() {
      if (this.chat.is_group) {
        return (this.chat.name || 'G').charAt(0).toUpperCase();
      }
      const other = this.chat.users?.find(u => u.id !== this.user.id);
      return other?.first_name ? other.first_name.charAt(0).toUpperCase() : 'U';
    },

    avatarColor() {
      const seed = (this.chat.name || this.chatName || '').charCodeAt(0) || 0;
      return AVATAR_COLORS[seed % AVATAR_COLORS.length];
    },

    otherUser() {
      if (this.chat.is_group) return null;
      return this.chat.users?.find(u => u.id !== this.user.id) || null;
    },

    isOtherOnline() {
      return this.otherUser ? this.isUserOnline(this.otherUser.id) : false;
    },

    statusText() {
      const typingUsers = this.getTypingUsers(this.chat.id);
      if (typingUsers.length > 0) {
        if (this.chat.is_group) {
          const who = this.chat.users?.find(u => u.id === typingUsers[0]);
          return who ? `${who.first_name} is typing…` : 'typing…';
        }
        return 'typing…';
      }

      if (this.chat.is_group) {
        const count = this.chat.users?.length || 0;
        return `${count} member${count !== 1 ? 's' : ''}`;
      }

      return this.isOtherOnline ? 'Online' : 'Offline';
    },

    onlineClass() {
      const typing = this.getTypingUsers(this.chat.id).length > 0;
      if (typing) return 'success--text';
      if (!this.chat.is_group && this.isOtherOnline) return 'success--text';
      return 'grey--text';
    }
  }
};
</script>

<style scoped>
.chat-header {
  position: relative;
  z-index: 1;
}
</style>
